import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from "../service/UsersService";

// מיפוי שם האירוע (עמודת name ב-userActivities) לתווית, צבע ואמוג'י תצוגתיים.
// ערכים בשימוש בפועל בבסיס הנתונים: Login, SetProgress, SaveUserDecisions,
// InsertUserDeposit, SetMarkedParagraphs. ניתן להוסיף עוד לפי הצורך.
const EVENT_CONFIG = {
  Login:              { emoji: '🔑', label: 'התחברות',    color: '#818CF8', bg: '#EEF2FF' },
  SetProgress:        { emoji: '✅', label: 'עדכון משימה', color: '#C9A84C', bg: '#FEF9EE' },
  SaveUserDecisions:  { emoji: '📋', label: 'החלטה',      color: '#FB923C', bg: '#FFF7ED' },
  InsertUserDeposit:  { emoji: '💰', label: 'צדקה',       color: '#34D399', bg: '#ECFDF5' },
  ReportPerformance:  { emoji: '📄', label: 'דו"ח',       color: '#60A5FA', bg: '#EFF6FF' },
  LogMusicPlay:       { emoji: '🎵', label: 'ניגון',       color: '#F472B6', bg: '#FDF2F8' },
  SetMarkedParagraphs:{ emoji: '🔖', label: 'סימניה',     color: '#8B5CF6', bg: '#F5F3FF' },
};
const DEFAULT_CONFIG = { emoji: '⭐', label: 'פעילות', color: '#A78BFA', bg: '#F5F3FF' };

const getEventConfig = (eventName) => EVENT_CONFIG[eventName] || DEFAULT_CONFIG;

// ── טווחי תאריכים מוכנים לבחירה מעל רשימת הפעילויות — ברירת המחדל (24 שעות)
// שומרת על אותה התנהגות שהייתה קודם (השרת גם מגביל TOP 500 כרשת ביטחון). ──
const RANGE_PRESETS = [
  { key: '24h',   label: '24 שעות אחרונות', hours: 24 },
  { key: '3d',    label: '3 ימים אחרונים',   hours: 24 * 3 },
  { key: 'week',  label: 'שבוע אחרון',       hours: 24 * 7 },
  { key: 'month', label: 'חודש אחרון',       hours: 24 * 30 },
  { key: 'custom',label: 'מותאם אישית' },
];

// ── ל-<input type="datetime-local"> — "YYYY-MM-DDTHH:mm" בזמן מקומי ──
const toDateTimeLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── לפרמטר שנשלח לשרת — "YYYY-MM-DD HH:mm:ss" בזמן מקומי, בלי 'Z'/UTC. שולחים
// זמן מקומי גולמי (לא toISOString) כי iTime בטבלה נשמר לפי GETDATE() המקומי
// של השרת — אם שולחים UTC, SQL Server פשוט מתעלם מה-'Z' בהמרה ל-datetime,
// מה שיוצר פער של כמה שעות מול הזמן שבאמת נשמר. ──
const toLocalSqlDateTime = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const groupByDay = (items) => {
  const groups = {};
  items.forEach((act) => {
    const dateStr = (act.iTime || '').substring(0, 10);
    const [y, m, d] = dateStr.split('-');
    if (!y) return;
    const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    let label = dateObj.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    if (dateObj.toDateString() === today.toDateString()) label = 'היום';
    else if (dateObj.toDateString() === yesterday.toDateString()) label = 'אתמול';
    if (!groups[label]) groups[label] = [];
    groups[label].push(act);
  });
  return groups;
};

const formatTime = (iTime) => (iTime || '').substring(11, 16);

// ── לתצוגת "פעילות אחרונה" בכרטיס משתמש — תאריך+שעה, כי בטווחים ארוכים
// (שבוע/חודש) שעה בלבד לא מספיקה כדי לדעת איזה יום. ──
const formatDateTime = (iTime) => {
  const s = iTime || '';
  if (!s) return '';
  const [, m, d] = s.substring(0, 10).split('-');
  return `${d}/${m} ${formatTime(s)}`;
};

const renderExtraData = (jsonData) => {
  try {
    if (!jsonData) return null;
    const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    const entries = Object.entries(data).filter(([k]) =>
      !['action', 'params', 'cityName'].includes(k) && data[k] != null && String(data[k]).length < 60
    );
    if (entries.length === 0) return null;
    return (
      <div style={s.extraData}>
        {entries.map(([k, v]) => (
          <span key={k} style={s.extraItem}><strong>{k}:</strong> {String(v)}</span>
        ))}
      </div>
    );
  } catch { return null; }
};

// ──────────────────────────────────
// Activity Timeline (שימוש כפול)
// ──────────────────────────────────
const ActivityList = ({ activities, onUserClick, showUserName = true }) => {
  const grouped = groupByDay(activities);
  if (Object.keys(grouped).length === 0)
    return <div style={s.noData}>אין פעילויות להצגה</div>;

  return (
    <div>
      {Object.entries(grouped).map(([day, items]) => (
        <div key={day} style={{ marginBottom: 28 }}>
          <div style={s.dayChip}>{day}</div>
          <div style={s.timeline}>
            {items.map((act, idx) => {
              const cfg = getEventConfig(act.eventName);
              return (
                <div key={act.id || idx} style={s.timelineRow}>
                  <div style={s.dotCol}>
                    <div style={{ ...s.dot, background: cfg.bg, border: `2px solid ${cfg.color}` }}>
                      <span style={{ fontSize: 14 }}>{cfg.emoji}</span>
                    </div>
                    {idx < items.length - 1 && <div style={{ ...s.line, background: cfg.color + '30' }} />}
                  </div>
                  <div style={s.card}>
                    <div style={s.cardTop}>
                      <div style={s.cardLeft}>
                        <span style={{ ...s.typeBadge, color: cfg.color, background: cfg.bg }}>
                          {cfg.label}
                        </span>
                        <span style={s.desc}>{act.eventDescription || act.eventName}</span>
                      </div>
                      <div style={s.cardRight}>
                        <span style={s.time}>{formatTime(act.iTime)}</span>
                        {showUserName && act.userName && (
                          <button
                            style={s.userBtn}
                            onClick={() => onUserClick && onUserClick(act)}
                          >
                            👤 {act.userName}
                          </button>
                        )}
                      </div>
                    </div>
                    {renderExtraData(act.extraData)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ──────────────────────────────────
// Main component
// ──────────────────────────────────
const ActivitiesTab = () => {
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null); // { userId, userName }
  const [userActivities, setUserActivities] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  const [filter, setFilter] = useState('all'); // all | Login | SetProgress | SaveUserDecisions | InsertUserDeposit

  const [viewMode, setViewMode] = useState('feed'); // feed | byUser
  const [userSearch, setUserSearch] = useState('');

  const [rangePreset, setRangePreset] = useState('24h');
  const [customFrom, setCustomFrom] = useState(() => toDateTimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [customTo, setCustomTo] = useState(() => toDateTimeLocal(new Date()));
  // ── טווח התאריכים ששימש בפועל לפעילויות הנטענות כרגע — נשמר כדי שקליק על
  // משתמש (drill-down ל"פעילויות של X") ישתמש באותו טווח בדיוק, ולא ישלוף
  // תמיד "7 ימים אחרונים" קבוע בלי קשר לבחירה למעלה. ──
  const [currentRange, setCurrentRange] = useState(null);

  const fetchActivities = async (range) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentRange(range);
      const res = await BOAPI.fetchAllActivities(range);
      if (res?.[0]?.[0]?.code === 0) setAllActivities(res[1] || []);
      else setError('שגיאה בטעינת הפעילויות');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── כל בחירת פריסט (חוץ מ"מותאם אישית") שולפת מיד; מותאם אישית שולף
  // רק בלחיצה על "החל" (למטה), כדי לא לשלוח בקשה על כל הקלדה בשדה. ──
  useEffect(() => {
    if (rangePreset === 'custom') return;
    const preset = RANGE_PRESETS.find(p => p.key === rangePreset);
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - preset.hours * 60 * 60 * 1000);
    fetchActivities({ fromDate: toLocalSqlDateTime(fromDate), toDate: toLocalSqlDateTime(toDate) });
  }, [rangePreset]);

  const handleApplyCustomRange = () => {
    if (!customFrom || !customTo) return;
    // customFrom/customTo כבר מגיעים מ-<input type="datetime-local"> כזמן מקומי
    // ("YYYY-MM-DDTHH:mm") — רק מחליפים את ה-T ומוסיפים שניות, בלי לעבור דרך
    // Date/toISOString (שהיה הופך את זה בטעות לזמן UTC). ──
    fetchActivities({ fromDate: `${customFrom.replace('T', ' ')}:00`, toDate: `${customTo.replace('T', ' ')}:00` });
  };

  const handleUserClick = async (act) => {
    setSelectedUser({ userId: act.userId, userName: act.userName });
    setUserLoading(true);
    setUserActivities([]);
    try {
      // אותו טווח תאריכים שנבחר למעלה (currentRange) — לא "7 ימים אחרונים"
      // קבוע, אחרת בחירת 24 שעות/3 ימים הייתה מציגה תמיד את אותה תוצאה.
      const res = await BOAPI.fetchUserActivities(act.userId, currentRange);
      if (res?.[0]?.[0]?.code === 0) setUserActivities(res[1] || []);
    } catch (e) { console.error(e); }
    finally { setUserLoading(false); }
  };

  const handleBack = () => { setSelectedUser(null); setUserActivities([]); };

  const filteredAll = filter === 'all'
    ? allActivities
    : allActivities.filter(a => a.eventName === filter);

  // ── ריכוז הפעילויות (בטווח התאריכים הנבחר) למשתמש אחד לכל שורה — נחשב מ-
  // allActivities שכבר ירדה מהשרת, כדי לא לבצע קריאה נוספת. ממוין לפי פעילות
  // אחרונה, כדי שמשתמשים פעילים יופיעו קודם. ──
  const usersSummary = useMemo(() => {
    const map = new Map();
    allActivities.forEach((act) => {
      if (!act.userId) return;
      if (!map.has(act.userId)) {
        map.set(act.userId, { userId: act.userId, userName: act.userName, count: 0, lastTime: act.iTime });
      }
      const entry = map.get(act.userId);
      entry.count += 1;
      if ((act.iTime || '') > (entry.lastTime || '')) entry.lastTime = act.iTime;
    });
    return Array.from(map.values()).sort((a, b) => (b.lastTime || '').localeCompare(a.lastTime || ''));
  }, [allActivities]);

  const filteredUsersSummary = userSearch.trim()
    ? usersSummary.filter(u => (u.userName || '').includes(userSearch.trim()))
    : usersSummary;

  const FILTER_OPTIONS = [
    { value: 'all',              label: 'הכל' },
    { value: 'Login',            label: '🔑 התחברות' },
    { value: 'SetProgress',      label: '✅ משימה' },
    { value: 'SaveUserDecisions',label: '📋 החלטה' },
    { value: 'InsertUserDeposit',label: '💰 צדקה' },
  ];

  // ── תצוגת משתמש בודד ──
  if (selectedUser) {
    return (
      <div style={s.container}>
        <div style={s.userHeader}>
          <button style={s.backBtn} onClick={handleBack}>← חזרה לכולם</button>
          <div style={s.userTitle}>
            <div style={s.userAvatar}>{(selectedUser.userName || '?')[0]}</div>
            <span style={s.userTitleName}>פעילויות של {selectedUser.userName}</span>
          </div>
        </div>
        {userLoading
          ? <div style={s.center}>טוען...</div>
          : <ActivityList activities={userActivities} showUserName={false} />
        }
      </div>
    );
  }

  // ── תצוגה ראשית ──
  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>פעילויות מערכת</h2>
        <span style={s.totalBadge}>{allActivities.length} בטווח שנבחר</span>
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div style={s.rangeRow}>
        {RANGE_PRESETS.map(opt => (
          <button
            key={opt.key}
            style={{ ...s.rangeBtn, ...(rangePreset === opt.key ? s.rangeBtnActive : {}) }}
            onClick={() => setRangePreset(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {rangePreset === 'custom' && (
        <div style={s.customRangeRow}>
          <label style={s.customLabel}>
            מתאריך
            <input type="datetime-local" style={s.customInput} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>
          <label style={s.customLabel}>
            עד תאריך
            <input type="datetime-local" style={s.customInput} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>
          <button style={s.applyBtn} onClick={handleApplyCustomRange}>החל</button>
        </div>
      )}

      <div style={s.viewToggleRow}>
        <button
          style={{ ...s.viewToggleBtn, ...(viewMode === 'feed' ? s.viewToggleBtnActive : {}) }}
          onClick={() => setViewMode('feed')}
        >
          🕒 כל הפעילויות
        </button>
        <button
          style={{ ...s.viewToggleBtn, ...(viewMode === 'byUser' ? s.viewToggleBtnActive : {}) }}
          onClick={() => setViewMode('byUser')}
        >
          👥 לפי משתמש
        </button>
      </div>

      {viewMode === 'feed' ? (
        <>
          <div style={s.filterRow}>
            {FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                style={{ ...s.filterBtn, ...(filter === opt.value ? s.filterBtnActive : {}) }}
                onClick={() => setFilter(opt.value)}
              >
                {opt.label}
                {opt.value !== 'all' && (
                  <span style={s.filterCount}>
                    {allActivities.filter(a => a.eventName === opt.value).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {loading
            ? <div style={s.center}>טוען פעילויות...</div>
            : <ActivityList activities={filteredAll} onUserClick={handleUserClick} />
          }
        </>
      ) : (
        <>
          <input
            type="text"
            placeholder="חיפוש לפי שם..."
            style={s.userSearchInput}
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />

          {loading ? (
            <div style={s.center}>טוען פעילויות...</div>
          ) : filteredUsersSummary.length === 0 ? (
            <div style={s.noData}>אין משתמשים עם פעילות בטווח שנבחר</div>
          ) : (
            <div style={s.usersGrid}>
              {filteredUsersSummary.map((u) => (
                <button key={u.userId} style={s.userCard} onClick={() => handleUserClick(u)}>
                  <div style={s.userCardAvatar}>{(u.userName || '?')[0]}</div>
                  <div style={s.userCardInfo}>
                    <span style={s.userCardName}>{u.userName || 'ללא שם'}</span>
                    <span style={s.userCardMeta}>{u.count} פעילויות · {formatDateTime(u.lastTime)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const s = {
  container:  { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  center:     { padding: 40, textAlign: 'center', color: '#999' },
  noData:     { padding: 40, textAlign: 'center', color: '#aaa', background: '#f8fafc', borderRadius: 8 },
  headerRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  title:      { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge: { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  errorBanner:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:   { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18, fontWeight: 'bold' },

  rangeRow:   { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  rangeBtn:   { padding: '7px 14px', borderRadius: 20, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#697386' },
  rangeBtnActive: { background: '#1a1f36', borderColor: '#1a1f36', color: '#fff' },

  customRangeRow: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20, padding: 14, background: '#f8fafc', borderRadius: 8, border: '1px dashed #e3e8ee' },
  customLabel:    { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#4f566b' },
  customInput:    { padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e3e8ee', fontSize: 13 },
  applyBtn:       { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1f6feb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, height: 34 },

  viewToggleRow:    { display: 'flex', gap: 8, marginBottom: 20, background: '#f1f3f8', borderRadius: 12, padding: 4, width: 'fit-content' },
  viewToggleBtn:    { padding: '8px 18px', borderRadius: 9, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#697386' },
  viewToggleBtnActive: { background: '#fff', color: '#1a1f36', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },

  userSearchInput: { width: '100%', maxWidth: 320, padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, marginBottom: 18, boxSizing: 'border-box' },
  usersGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 },
  userCard:   { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'right', fontFamily: 'inherit' },
  userCardAvatar: { width: 40, height: 40, borderRadius: '50%', background: '#1f6feb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 'bold', flexShrink: 0 },
  userCardInfo: { display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' },
  userCardName: { fontSize: 14, fontWeight: 700, color: '#1a1f36', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userCardMeta: { fontSize: 12, color: '#a3acb9' },

  filterRow:  { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  filterBtn:  { padding: '7px 14px', borderRadius: 20, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#697386', display: 'flex', alignItems: 'center', gap: 6 },
  filterBtnActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  filterCount:{ background: 'rgba(255,255,255,0.3)', borderRadius: 10, padding: '0 7px', fontSize: 11 },

  dayChip:    { display: 'inline-block', background: '#e3e8ee', color: '#4f566b', fontSize: 12, fontWeight: 700, padding: '3px 14px', borderRadius: 20, marginBottom: 14 },
  timeline:   { display: 'flex', flexDirection: 'column', paddingRight: 6 },
  timelineRow:{ display: 'flex', gap: 14, marginBottom: 4 },
  dotCol:     { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 42, flexShrink: 0, paddingTop: 4 },
  dot:        { width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, flexShrink: 0 },
  line:       { flex: 1, width: 2, margin: '4px 0', minHeight: 20 },
  card:       { flex: 1, background: '#fff', border: '1px solid #e3e8ee', borderRadius: 10, padding: '10px 14px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' },
  cardTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 },
  cardLeft:   { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardRight:  { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 },
  typeBadge:  { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8 },
  desc:       { fontSize: 14, color: '#1a1f36', fontWeight: 600 },
  time:       { fontSize: 11, color: '#a3acb9', fontWeight: 600 },
  userBtn:    { background: '#f0f4ff', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 8, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  extraData:  { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 6, border: '1px dashed #e3e8ee' },
  extraItem:  { fontSize: 11, color: '#4f566b' },

  userHeader: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, padding: '14px 0', borderBottom: '1px solid #e3e8ee' },
  backBtn:    { padding: '8px 16px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  userTitle:  { display: 'flex', alignItems: 'center', gap: 10 },
  userAvatar: { width: 40, height: 40, borderRadius: '50%', background: '#1f6feb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 'bold' },
  userTitleName: { fontSize: 18, fontWeight: 700, color: '#1a1f36' },
};

export default ActivitiesTab;
